import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, RefreshCw, AlertCircle, ArrowLeft } from 'lucide-react';
import AvailabilityCalendar from './AvailabilityCalendar';
import BookingConfirmationModal from './BookingConfirmationModal';
import TherapyTypeCard from './TherapyTypeCard';
import { availabilityAPI, appointmentAPI, partnerAPI, razorpayAPI } from '../../services/api';
import { initializeRazorpayCheckout } from '../../utils/razorpayHelper';

const ClientAvailabilityTab = ({ userId, partners, defaultPartnerId = null }) => {
  const navigate = useNavigate();
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [feeSettings, setFeeSettings] = useState(null);
  const [therapyTypes, setTherapyTypes] = useState([]);
  const [selectedTherapyType, setSelectedTherapyType] = useState(null);
  const [filteredSlots, setFilteredSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);

  useEffect(() => {
    // Use defaultPartnerId if provided, otherwise auto-select partner if only one exists
    if (defaultPartnerId && partners) {
      const partner = partners.find(p => p.id === defaultPartnerId);
      if (partner) {
        setSelectedPartner(partner);
      } else if (partners.length === 1) {
        setSelectedPartner(partners[0]);
      }
    } else if (partners && partners.length === 1) {
      setSelectedPartner(partners[0]);
    }
  }, [partners, defaultPartnerId]);

  useEffect(() => {
    if (selectedPartner) {
      loadTherapyTypes(selectedPartner.id);
      loadFeeSettings(selectedPartner.id);
      loadBookedSlots(selectedPartner.id);
    }
  }, [selectedPartner]);

  /**
   * Load fee settings for selected partner
   */
  const loadFeeSettings = async (partnerId) => {
    try {
      const response = await partnerAPI.getFeeSettings(partnerId);
      setFeeSettings(response.data.feeSettings);
    } catch (error) {
      console.error('Failed to load fee settings:', error);
      // Continue without fee settings
      setFeeSettings(null);
    }
  };

  /**
   * Load available therapy types for the partner
   */
  const loadTherapyTypes = async (partnerId) => {
    try {
      setLoading(true);
      const response = await availabilityAPI.getTherapyTypes(partnerId);
      setTherapyTypes(response.data.therapyTypes || []);
    } catch (error) {
      console.error('Failed to load therapy types:', error);
      alert('Failed to load therapy types. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Load booked slots for the user
   */
  const loadBookedSlots = async (partnerId) => {
    try {
      setLoading(true);
      const response = await availabilityAPI.getBookedSlots(partnerId);
      setBookedSlots(response.data.slots || []);
    } catch (error) {
      console.error('Failed to load booked slots:', error);
      setBookedSlots([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle therapy type card selection
   */
  const handleTherapyTypeSelect = async (therapyType) => {
    try {
      setLoading(true);
      setSelectedTherapyType(therapyType);

      // Fetch filtered slots and booked slots in parallel
      const [slotsResponse, bookedResponse] = await Promise.all([
        availabilityAPI.getBookableSlotsForTherapyType(
          selectedPartner.id,
          therapyType
        ),
        availabilityAPI.getBookedSlots(selectedPartner.id)
      ]);

      const availableSlots = slotsResponse.data.slots || [];
      const freshBookedSlots = bookedResponse.data.slots || [];

      // Update booked slots state with fresh data
      setBookedSlots(freshBookedSlots);

      // Filter booked slots for this therapy type
      const bookedSlotsForTherapy = freshBookedSlots.filter(
        slot => slot.therapy_type === therapyType
      );

      // Combine both arrays
      const allSlots = [...bookedSlotsForTherapy, ...availableSlots];

      setFilteredSlots(allSlots);
    } catch (error) {
      console.error('Failed to load bookable slots:', error);
      alert('Failed to load available slots. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Go back to therapy type selection
   */
  const handleBackToTherapyTypes = () => {
    setSelectedTherapyType(null);
    setFilteredSlots([]);
  };

  /**
   * Reload current view (therapy types or calculated slots)
   */
  const reloadCurrentView = async () => {
    if (selectedTherapyType) {
      // Reload calculated slots for selected therapy type
      await handleTherapyTypeSelect(selectedTherapyType);
    } else {
      // Reload therapy types
      await loadTherapyTypes(selectedPartner.id);
    }
  };

  /**
   * Handle slot selection for booking
   */
  const handleBookSlot = (slot) => {
    setSelectedSlot(slot);
    setShowBookingModal(true);
  };

  /**
   * Confirm booking - with payment flow if session fee exists
   */
  const confirmBooking = async (sessionType) => {
    try {
      setBookingLoading(true);

      // Get therapy type data from the selected therapy type
      const selectedTherapyData = therapyTypes.find(t => t.therapy_type === selectedTherapyType);
      const sessionFee = parseFloat(selectedTherapyData?.fee) || 0;
      const currency = selectedTherapyData?.currency || 'INR';

      // Debug logging
      console.log('[BOOKING] Therapy type:', selectedTherapyType);
      console.log('[BOOKING] Session fee:', sessionFee);
      console.log('[BOOKING] Currency:', currency);
      console.log('[BOOKING] Session type:', sessionType);

      // If session fee exists, initiate payment flow
      if (sessionFee > 0) {
        console.log('[BOOKING] Initiating payment flow for session fee:', sessionFee);
        await handleBookingWithPayment(sessionType, sessionFee, currency);
      } else {
        // Direct booking without payment
        console.log('[BOOKING] No session fee, proceeding with direct booking');
        await handleDirectBooking(sessionType);
      }
    } catch (error) {
      console.error('Failed to book slot:', error);
      const errorMessage = error.response?.data?.error || 'Failed to book appointment';
      alert(errorMessage);
      setBookingLoading(false);
    }
  };

  /**
   * Handle booking with payment
   */
  const handleBookingWithPayment = async (sessionType, sessionFee, currency) => {
    try {
      const orderData = {
        slot_id: selectedSlot.id,
        partner_id: selectedPartner.id,
        session_type: sessionType,
        therapy_type: selectedTherapyType
      };

      const orderResponse = await razorpayAPI.createBookingOrder(orderData);

      // Check if test mode - skip payment flow
      if (orderResponse.data.skip_payment || orderResponse.data.test_mode) {
        const verifyResponse = await razorpayAPI.verifyBookingPayment({
          razorpay_order_id: null,
          razorpay_payment_id: null,
          razorpay_signature: null,
          slot_id: selectedSlot.id,
          session_type: sessionType
        });

        if (verifyResponse.data.booking_confirmed) {
          const bookingData = {
            session_type: sessionType
          };
          const bookingResponse = await availabilityAPI.bookSlot(selectedSlot.id, bookingData);

          setShowBookingModal(false);
          setBookingLoading(false);

          if (bookingResponse.data.google_conflict) {
            alert(
              'Booking confirmed! (Test Mode - Payment Skipped)\n\n' +
              'Note: There was a conflict with Google Calendar, so the appointment was not added to the calendar. ' +
              'However, your booking has been confirmed in the system.'
            );
          } else {
            alert('Booking confirmed! (Test Mode - Payment Skipped) The appointment has been added to your calendar.');
          }

          reloadCurrentView();
        } else {
          throw new Error('Booking verification failed');
        }
        return;
      }

      // Normal flow: Proceed with Razorpay payment
      const order = orderResponse.data.order;

      const userDetails = {
        name: '',
        email: '',
        contact: ''
      };

      const paymentResult = await initializeRazorpayCheckout(order, {
        name: 'TheraP Track',
        description: 'Session Fee Payment',
        prefill: userDetails
      });

      const verifyResponse = await razorpayAPI.verifyBookingPayment({
        razorpay_order_id: paymentResult.razorpay_order_id,
        razorpay_payment_id: paymentResult.razorpay_payment_id,
        razorpay_signature: paymentResult.razorpay_signature,
        slot_id: selectedSlot.id,
        session_type: sessionType
      });

      if (verifyResponse.data.booking_confirmed) {
        const bookingData = {
          session_type: sessionType
        };
        const bookingResponse = await availabilityAPI.bookSlot(selectedSlot.id, bookingData);

        setShowBookingModal(false);
        setBookingLoading(false);

        navigate('/payment-success', {
          state: {
            payment: verifyResponse.data.payment,
            booking: {
              slot_id: selectedSlot.id,
              appointment_id: bookingResponse.data.appointment_id,
              google_conflict: bookingResponse.data.google_conflict
            }
          },
          replace: true
        });
      } else {
        throw new Error('Payment verification failed');
      }
    } catch (error) {
      setBookingLoading(false);
      if (error.message === 'Payment cancelled by user') {
        setShowBookingModal(false);
        alert('Payment cancelled. Your booking was not completed.');
      } else {
        throw error;
      }
    }
  };

  /**
   * Handle direct booking without payment
   */
  const handleDirectBooking = async (sessionType) => {
    const bookingData = {
      session_type: sessionType
    };

    const response = await availabilityAPI.bookSlot(selectedSlot.id, bookingData);

    setShowBookingModal(false);
    setBookingLoading(false);

    // Check if there was a Google Calendar conflict
    if (response.data.google_conflict) {
      alert(
        'Booking successful!\n\n' +
        'Note: There was a conflict with Google Calendar, so the appointment was not added to the calendar. ' +
        'However, your booking has been confirmed in the system.'
      );
    } else {
      alert('Appointment booked successfully! The appointment has been added to your calendar.');
    }

    // Reload slots to show updated availability
    reloadCurrentView();
  };

  /**
   * Cancel booking modal
   */
  const cancelBooking = () => {
    setShowBookingModal(false);
    setSelectedSlot(null);
    setBookingLoading(false);
  };

  /**
   * Handle payment for remaining balance
   */
  const handlePayRemaining = async (slot) => {
    try {
      setPaymentLoading(true);

      // Create Razorpay order for remaining payment
      const orderResponse = await razorpayAPI.createRemainingPaymentOrder({
        slot_id: slot.id
      });

      // Check if test mode - skip payment flow
      if (orderResponse.data.skip_payment || orderResponse.data.test_mode) {
        // Test mode: Skip payment and directly verify
        const verifyResponse = await razorpayAPI.verifyRemainingPayment({
          razorpay_order_id: null,
          razorpay_payment_id: null,
          razorpay_signature: null,
          slot_id: slot.id
        });

        if (verifyResponse.data.payment_confirmed) {
          setPaymentLoading(false);
          alert('Payment successful! Your booking is now confirmed.');
          // Reload slots to show updated status
          reloadCurrentView();
        } else {
          throw new Error('Payment verification failed');
        }
        return;
      }

      // Normal flow: Proceed with Razorpay payment
      const order = orderResponse.data.order;

      // Get user details for prefill
      const userDetails = {
        name: '',
        email: '',
        contact: ''
      };

      // Initialize Razorpay checkout
      const paymentResult = await initializeRazorpayCheckout(order, {
        name: 'TheraP Track',
        description: 'Remaining Payment for Booking',
        prefill: userDetails
      });

      // Verify payment
      const verifyResponse = await razorpayAPI.verifyRemainingPayment({
        razorpay_order_id: paymentResult.razorpay_order_id,
        razorpay_payment_id: paymentResult.razorpay_payment_id,
        razorpay_signature: paymentResult.razorpay_signature,
        slot_id: slot.id
      });

      if (verifyResponse.data.payment_confirmed) {
        setPaymentLoading(false);
        alert('Payment successful! Your booking is now confirmed.');
        // Reload slots to show updated status
        reloadCurrentView();
      } else {
        throw new Error('Payment verification failed');
      }
    } catch (error) {
      setPaymentLoading(false);
      if (error.message === 'Payment cancelled by user') {
        alert('Payment cancelled. Please try again when ready.');
      } else {
        console.error('Failed to process remaining payment:', error);
        const errorMessage = error.response?.data?.error || 'Failed to process payment';
        alert(errorMessage);
      }
    }
  };

  // Handle case where no partners are available
  if (!partners || partners.length === 0) {
    return (
      <div className="bg-white dark:bg-dark-bg-primary rounded-lg shadow-md p-8 text-center">
        <AlertCircle className="h-12 w-12 text-gray-300 dark:text-dark-text-tertiary mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-2">No Therapist Assigned</h3>
        <p className="text-gray-500 dark:text-dark-text-secondary">
          You don't have a therapist assigned yet. Please contact support for assistance.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-dark-text-primary mb-2">Book an Appointment</h2>
        <p className="text-gray-600 dark:text-dark-text-secondary">
          View your therapist's available time slots and book an appointment.
        </p>
      </div>

      {/* Partner Selector (if multiple partners) - Hidden when user has exactly 2 therapists (already shown in dashboard) */}
      {partners.length > 2 && (
        <div className="bg-white dark:bg-dark-bg-primary rounded-lg shadow-md p-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-text-primary mb-2">
            Select Therapist
          </label>
          <select
            value={selectedPartner?.id || ''}
            onChange={(e) => {
              const partner = partners.find(p => p.id === parseInt(e.target.value));
              setSelectedPartner(partner);
            }}
            className="w-full md:w-1/2 px-3 py-2 border border-gray-300 dark:border-dark-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-dark-bg-secondary dark:text-dark-text-primary"
          >
            <option value="">Choose a therapist</option>
            {partners.map(partner => (
              <option key={partner.id} value={partner.id}>
                {partner.name}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Selected Partner Info */}
      {selectedPartner && (
        <div className="bg-primary-50 dark:bg-dark-bg-secondary border border-primary-200 dark:border-dark-border rounded-lg p-4 flex items-center justify-between">
          <div>
            <p className="text-sm text-primary-700 dark:text-dark-text-secondary">Booking with</p>
            <p className="font-semibold text-primary-900 dark:text-dark-text-primary">{selectedPartner.name}</p>
          </div>
          <button
            onClick={() => reloadCurrentView()}
            disabled={loading}
            className="p-2 text-primary-700 dark:text-dark-primary-400 hover:bg-primary-100 dark:hover:bg-dark-bg-tertiary rounded-md transition-colors disabled:opacity-50"
            title="Refresh slots"
          >
            <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      )}

      {/* Therapy Type Selection / Calendar View */}
      {!selectedPartner ? (
        <div className="bg-white dark:bg-dark-bg-primary rounded-lg shadow-md p-8 text-center">
          <Calendar className="h-12 w-12 text-gray-300 dark:text-dark-text-tertiary mx-auto mb-3" />
          <p className="text-gray-500 dark:text-dark-text-secondary">Please select a therapist to view available therapy types.</p>
        </div>
      ) : !selectedTherapyType ? (
        // Show therapy type cards
        <>
          <div className="bg-primary-50 dark:bg-dark-bg-secondary border border-primary-200 dark:border-dark-border rounded-lg p-4 mb-4">
            <p className="text-sm text-primary-700 dark:text-dark-text-secondary">
              Booking with <span className="font-semibold">{selectedPartner.name}</span>
            </p>
            <p className="text-xs text-primary-600 dark:text-dark-text-tertiary mt-1">
              Select a therapy type below to view available slots
            </p>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 text-gray-400 dark:text-dark-text-tertiary mx-auto mb-3 animate-spin" />
              <p className="text-gray-500 dark:text-dark-text-secondary">Loading therapy types...</p>
            </div>
          ) : therapyTypes.length === 0 ? (
            <div className="bg-white dark:bg-dark-bg-primary rounded-lg shadow-md p-8 text-center">
              <p className="text-gray-500 dark:text-dark-text-secondary">
                No therapy types available. Please contact your therapist.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {therapyTypes.map(therapyType => (
                <TherapyTypeCard
                  key={therapyType.therapy_type}
                  therapyType={therapyType}
                  onSelect={handleTherapyTypeSelect}
                />
              ))}
            </div>
          )}
        </>
      ) : (
        // Show calculated slots for selected therapy type
        <>
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow-md p-4 mb-4">
            <button
              onClick={handleBackToTherapyTypes}
              className="inline-flex items-center px-4 py-2 mb-3 bg-gray-100 dark:bg-dark-bg-tertiary text-gray-700 dark:text-dark-text-primary border border-gray-300 dark:border-dark-border rounded-md hover:bg-gray-200 dark:hover:bg-dark-bg-primary transition-colors font-medium"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back to Therapy Types
            </button>
            <div className="bg-primary-50 dark:bg-dark-bg-tertiary border border-primary-200 dark:border-primary-700 rounded-lg p-3">
              <p className="text-sm text-primary-700 dark:text-dark-text-secondary">
                Available slots for{' '}
                <span className="font-semibold">
                  {selectedTherapyType.charAt(0).toUpperCase() + selectedTherapyType.slice(1)} Therapy
                </span>
              </p>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 text-gray-400 dark:text-dark-text-tertiary mx-auto mb-3 animate-spin" />
              <p className="text-gray-500 dark:text-dark-text-secondary">Loading available slots...</p>
            </div>
          ) : (
            <AvailabilityCalendar
              slots={filteredSlots}
              onBook={handleBookSlot}
              onPayRemaining={handlePayRemaining}
              viewMode="client"
            />
          )}
        </>
      )}

      {/* Booking Confirmation Modal */}
      {showBookingModal && selectedSlot && selectedTherapyType && (
        <BookingConfirmationModal
          slot={selectedSlot}
          partnerName={selectedPartner.name}
          feeSettings={feeSettings}
          therapyType={therapyTypes.find(t => t.therapy_type === selectedTherapyType)}
          onConfirm={confirmBooking}
          onCancel={cancelBooking}
          loading={bookingLoading}
        />
      )}
    </div>
  );
};

export default ClientAvailabilityTab;
